import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { OutlinedBadge } from '@tms/shared-ui';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  downloadDashboardCsv,
  formatMoney,
  getAdminDashboard,
  type ComparisonMetric,
  type DashboardAlertItem,
  type DashboardData,
} from '../services/adminDashboardApi';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';

const formatChange = (m?: ComparisonMetric) => {
  if (!m) return null;
  const sign = m.changePct > 0 ? '+' : '';
  return `${sign}${m.changePct}% vs prev period`;
};

const MetricCard: React.FC<{
  label: string;
  value: number | string;
  comparison?: ComparisonMetric;
  hint?: string;
}> = ({ label, value, comparison, hint }) => (
  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%' }}>
    <Tooltip title={hint || ''} disableHoverListener={!hint}>
      <Typography variant="h4" color="primary" fontWeight="bold">
        {value}
      </Typography>
    </Tooltip>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {comparison ? (
      <Typography
        variant="caption"
        sx={{ color: comparison.changePct >= 0 ? 'success.main' : 'error.main' }}
      >
        {formatChange(comparison)}
      </Typography>
    ) : null}
  </Paper>
);

type PeriodPreset = 'scholar' | 'scholar_last';

function scholarYearStart(now = new Date(), offset = 0) {
  return (now.getUTCMonth() >= 8 ? now.getUTCFullYear() : now.getUTCFullYear() - 1) + offset;
}

function fillDailySeries<T extends Record<string, number | string>>(
  points: T[],
  startIso: string | undefined,
  endIso: string | undefined,
  valueKey: string,
): Array<Record<string, string | number>> {
  if (!startIso || !endIso || !points) return points || [];
  const byDate = new Map(
    points.map((point) => [String(point.date).slice(0, 10), Number(point[valueKey] || 0)]),
  );
  const start = new Date(`${startIso.slice(0, 10)}T12:00:00Z`);
  const end = new Date(`${endIso.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return points;
  }
  const filled: Array<Record<string, string | number>> = [];
  for (let cursor = new Date(start); cursor <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    const date = cursor.toISOString().slice(0, 10);
    filled.push({ date, [valueKey]: byDate.get(date) ?? 0 });
  }
  return filled;
}

function formatChartDay(date: string) {
  const parsed = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatChartTick(date: string) {
  const parsed = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const TimeSeriesLineChart: React.FC<{
  data: Array<Record<string, string | number>>;
  valueKey: string;
  name: string;
  color: string;
  formatValue?: (value: number) => string;
  allowDecimals?: boolean;
}> = ({ data, valueKey, name, color, formatValue, allowDecimals = false }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={28} tickFormatter={formatChartTick} />
      <YAxis allowDecimals={allowDecimals} width={44} />
      <RechartsTooltip
        labelFormatter={(label) => formatChartDay(String(label))}
        formatter={(value: number) => [formatValue ? formatValue(value) : value, name]}
      />
      <Bar
        dataKey={valueKey}
        name={name}
        fill={color}
        maxBarSize={16}
        isAnimationActive={false}
      />
    </BarChart>
  </ResponsiveContainer>
);

const GlobalDashboardPage: React.FC = () => {
  const { navigateWithLang } = useLanguage();
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('scholar');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const [compare, setCompare] = useState(true);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);

  const dashboardParams = useMemo(
    () => ({ preset: periodPreset, compare, groupBy }),
    [periodPreset, compare, groupBy],
  );

  const days = data?.periodDays ?? 365;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminDashboard(dashboardParams);
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [dashboardParams]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadDashboardCsv(dashboardParams);
    } catch (e: any) {
      setError(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const registrationFunnelChart = useMemo(() => {
    const f = data?.users.registrationFunnel;
    if (!f) return [];
    return [
      { name: 'Registered', value: f.registered },
      { name: 'Verified', value: f.verified },
      { name: 'First login', value: f.firstLogin },
      { name: 'Doc viewed', value: f.firstDocumentViewed },
      { name: 'Trial', value: f.trialStarted },
      { name: 'Paid', value: f.paidSubscription },
    ].filter((r) => r.value > 0);
  }, [data]);

  const periodStart = data?.period?.start;
  const periodEnd = data?.period?.end;
  const fillDaily = groupBy === 'day';

  const signupsSeries = useMemo(
    () => (fillDaily
      ? fillDailySeries(data?.users.signupsOverTime || [], periodStart, periodEnd, 'count')
      : data?.users.signupsOverTime || []),
    [data, fillDaily, periodStart, periodEnd],
  );
  const sessionsSeries = useMemo(
    () => (fillDaily
      ? fillDailySeries(data?.sessions.overTime || [], periodStart, periodEnd, 'count')
      : data?.sessions.overTime || []),
    [data, fillDaily, periodStart, periodEnd],
  );
  const revenueSeries = useMemo(
    () => (fillDaily
      ? fillDailySeries(data?.revenue?.overTime || [], periodStart, periodEnd, 'amount')
      : data?.revenue?.overTime || []),
    [data, fillDaily, periodStart, periodEnd],
  );
  const activeSeries = useMemo(
    () => (fillDaily
      ? fillDailySeries(data?.users.activeOverTime || [], periodStart, periodEnd, 'count')
      : data?.users.activeOverTime || []),
    [data, fillDaily, periodStart, periodEnd],
  );

  const subscriptionChart = useMemo(() => {
    if (!data?.subscriptionsV2?.uniqueUsers) {
      if (!data) return [];
      return [
        { name: 'Active', value: data.subscriptions.active },
        { name: 'Trial', value: data.subscriptions.trial },
        { name: 'Canceled', value: data.subscriptions.canceled },
        { name: 'Expired', value: data.subscriptions.expired },
        { name: 'Grace', value: data.subscriptions.grace_period },
      ].filter((r) => r.value > 0);
    }
    const u = data.subscriptionsV2.uniqueUsers;
    return [
      { name: 'Paid', value: u.activePaid },
      { name: 'Trial', value: u.trial },
      { name: 'Grace', value: u.grace_period },
      { name: 'Ended', value: u.canceledOrExpired },
    ].filter((r) => r.value > 0);
  }, [data]);

  const refreshedLabel = data?.refreshedAt
    ? new Date(data.refreshedAt).toLocaleString()
    : '—';

  const handleAlertClick = (item: DashboardAlertItem) => {
    if (!item.link) return;
    navigateWithLang(item.link);
  };

  const alertSeverity = (s: DashboardAlertItem['severity']) => {
    if (s === 'error') return 'error' as const;
    if (s === 'warning') return 'warning' as const;
    return 'info' as const;
  };

  if (loading && !data) {
    return (
      <ContentPageLayout>
      <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
        <CircularProgress />
        <Typography variant="caption" color="text.secondary">Loading dashboard…</Typography>
      </Box>
      </ContentPageLayout>
    );
  }

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" color="primary" fontWeight="bold">
            Global dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fleet-wide users, subscriptions, sessions, and document engagement.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Refreshed: {refreshedLabel}
            {loading ? ' · updating…' : ''}
          </Typography>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
          <TextField
            select
            size="small"
            label="Period"
            value={periodPreset === 'scholar_last' ? 'scholar_last' : 'scholar'}
            onChange={(e) => setPeriodPreset(e.target.value as PeriodPreset)}
            sx={{ minWidth: 230 }}
          >
            <MenuItem value="scholar">
              Current scholar year ({scholarYearStart()}–{scholarYearStart() + 1})
            </MenuItem>
            <MenuItem value="scholar_last">
              Last scholar year ({scholarYearStart(new Date(), -1)}–{scholarYearStart(new Date(), -1) + 1})
            </MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Charts"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="day">Daily</MenuItem>
            <MenuItem value="week">Weekly</MenuItem>
            <MenuItem value="month">Monthly</MenuItem>
          </TextField>
          <FormControlLabel
            control={<Checkbox size="small" checked={compare} onChange={(e) => setCompare(e.target.checked)} />}
            label={<Typography variant="body2">Compare</Typography>}
          />
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}

      {data?.alerts?.items?.length ? (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {data.alerts.items.map((item) => (
            <Alert
              key={item.type}
              severity={alertSeverity(item.severity)}
              sx={{ cursor: item.link ? 'pointer' : 'default' }}
              onClick={() => handleAlertClick(item)}
            >
              {item.label}
              {item.link ? (
                <Typography component="span" variant="caption" sx={{ ml: 1, textDecoration: 'underline' }}>
                  View
                </Typography>
              ) : null}
            </Alert>
          ))}
        </Box>
      ) : null}

      {data ? (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={4} md>
              <MetricCard
                label="Signups"
                value={data.comparison?.signups?.current ?? data.users.signupsOverTime.reduce((s, r) => s + r.count, 0)}
                comparison={data.comparison?.signups}
                hint="New accounts created in the selected period"
              />
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard
                label="Active (period)"
                value={data.users.activeInPeriod ?? data.users.active30d}
                comparison={data.comparison?.activeInPeriod}
                hint="Users with activity in the selected period"
              />
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard
                label="Sessions (period)"
                value={data.sessions.lastPeriod}
                comparison={data.comparison?.sessionsInPeriod}
                hint="Course sessions in the selected period"
              />
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard
                label="Total paid users (period)"
                value={data.users.paidUsersInPeriod ?? data.subscriptionsV2?.uniqueUsers.activePaid ?? '—'}
                comparison={data.comparison?.paidUsersInPeriod}
                hint="Unique users with paid access during the selected period"
              />
            </Grid>
            <Grid item xs={6} sm={4} md>
              <MetricCard
                label="Trial users (current)"
                value={data.subscriptionsV2?.uniqueUsers.trial ?? '—'}
                hint="Unique users on an active trial now"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: 320 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Signups over time
                </Typography>
                {signupsSeries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                    No signups in this period.
                  </Typography>
                ) : (
                  <TimeSeriesLineChart
                    data={signupsSeries}
                    valueKey="count"
                    name="Signups"
                    color="#1976d2"
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: 320 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Course sessions over time
                </Typography>
                {sessionsSeries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                    No sessions in this period.
                  </Typography>
                ) : (
                  <TimeSeriesLineChart
                    data={sessionsSeries}
                    valueKey="count"
                    name="Sessions"
                    color="#2e7d32"
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: 320 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Revenue over time (net)
                </Typography>
                {revenueSeries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                    No revenue in this period.
                  </Typography>
                ) : (
                  <TimeSeriesLineChart
                    data={revenueSeries}
                    valueKey="amount"
                    name="Revenue"
                    color="#6a1b9a"
                    allowDecimals
                    formatValue={(value) => formatMoney(value, data.revenue?.currency)}
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: 320 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Active users over time
                </Typography>
                {activeSeries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                    No activity in this period.
                  </Typography>
                ) : (
                  <TimeSeriesLineChart
                    data={activeSeries}
                    valueKey="count"
                    name="Active users"
                    color="#0288d1"
                  />
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Users by role
                </Typography>
                <Table size="small">
                  <TableBody>
                    {data.users.byRole.map((r) => (
                      <TableRow key={r.role} hover sx={{ cursor: 'pointer' }} onClick={() => navigateWithLang(`/users?role=${encodeURIComponent(r.role)}`)}>
                        <TableCell>{r.role}</TableCell>
                        <TableCell align="right">{r.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Registration funnel (period)
                </Typography>
                {registrationFunnelChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={registrationFunnelChart} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="#1565c0" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">No registrations in period</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Subscription funnel (unique users)
                </Typography>
                {subscriptionChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={subscriptionChart} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={70} />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill="#ed6c02" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">No subscription data</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Users by status
                </Typography>
                <Table size="small">
                  <TableBody>
                    {data.users.byStatus.map((s) => (
                      <TableRow key={s.status} hover sx={{ cursor: 'pointer' }} onClick={() => navigateWithLang(`/users?status=${encodeURIComponent(s.status)}`)}>
                        <TableCell>{s.status}</TableCell>
                        <TableCell align="right">{s.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>

          {data.subscriptionsV2?.trialsExpiringSoon?.length ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Trials expiring soon (7 days)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Trial ends</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.subscriptionsV2.trialsExpiringSoon.map((t) => (
                    <TableRow key={t.subscriptionId}>
                      <TableCell>{t.nickname || t.email || t.userId}</TableCell>
                      <TableCell>{t.planName || '—'}</TableCell>
                      <TableCell>{t.trialEndDate ? new Date(t.trialEndDate).toLocaleDateString() : '—'}</TableCell>
                      <TableCell align="right">
                        {t.userId ? (
                          <Link component="button" type="button" onClick={() => navigateWithLang(`/users/${t.userId}?tab=subscription`)}>
                            View
                          </Link>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Top documents ({days}d)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell align="right">Views</TableCell>
                      <TableCell align="right">Minutes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.topDocuments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No tracking data — engagement requires user tracking sessions.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.topDocuments.map((d) => (
                        <TableRow key={d.documentCode}>
                          <TableCell>
                            <Link
                              component="button"
                              type="button"
                              onClick={() => navigateWithLang(`/page-editor?q=${encodeURIComponent(d.documentCode)}`)}
                            >
                              {d.documentCode}
                            </Link>
                          </TableCell>
                          <TableCell align="right">{d.views}</TableCell>
                          <TableCell align="right">{d.totalTimeMinutes}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Teacher workload
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Teacher</TableCell>
                      <TableCell align="right">Students</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.teacherWorkload.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary">No teachers found.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.teacherWorkload.map((t) => (
                        <TableRow key={t.teacherId} hover>
                          <TableCell>
                            <Link
                              component="button"
                              type="button"
                              onClick={() => navigateWithLang(`/users/${t.teacherId}`)}
                            >
                              {t.nickname || t.email || t.teacherId}
                            </Link>
                            {t.studentCount > 0 ? (
                              <OutlinedBadge
                                size="small"
                                label="students"
                                color="primary"
                                sx={{ ml: 1 }}
                                onClick={() => navigateWithLang(`/users?teacherId=${t.teacherId}`)}
                              />
                            ) : null}
                          </TableCell>
                          <TableCell align="right">{t.studentCount}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : null}
    </Box>
    </ContentPageLayout>
  );
};

export default GlobalDashboardPage;
